import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiDownload, FiXCircle } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

const FiXComponent = FiX as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;
const FiDownloadComponent = FiDownload as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;
const FiXCircleComponent = FiXCircle as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

interface ExportPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    exportId: string;
    snapshotId: string;
    segmentId: string;
    onDownload: () => void;
}

const ExportPreviewModal: React.FC<ExportPreviewModalProps> = ({
    isOpen,
    onClose,
    exportId,
    snapshotId,
    segmentId,
    onDownload,
}) => {
    const [status, setStatus] = useState<string>('PENDING');
    const [counts, setCounts] = useState<{ total: number } | null>(null);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [exportMembers, setExportMembers] = useState<any[]>([]);
    const [membersTotalCount, setMembersTotalCount] = useState(0);
    const [membersPage, setMembersPage] = useState(0);
    // const [isReady, setIsReady] = useState(false);
    const membersPerPage = 6;
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isPollingStoppedRef = useRef<boolean>(false);
    const hasFetchedMembersRef = useRef<boolean>(false);

    // Fetch export members using the snapshot members API
    const fetchExportMembers = async (page: number = 0) => {
        try {
            setIsLoading(true);
            const { token, tenantId, userId } = getAuthContext();
            if (!token) {
                toast.error('Authentication required');
                setIsLoading(false);
                return;
            }

            const response = await fetch(
                `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots/${snapshotId}/members?size=${membersPerPage}&page=${page}`,
                {
                    method: 'GET',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            setExportMembers(data.content || []);
            setMembersTotalCount(data.totalCount || 0);
            setMembersPage(page);
            // setIsReady(true);
            hasFetchedMembersRef.current = true;

            // Stop polling once members are loaded and displayed
            stopPolling();

        } catch (error) {
            // console.error('❌ Failed to fetch export members:', error);
            toast.error('Failed to load export preview');
        } finally {
            setIsLoading(false);
        }
    };

    // Stop polling
    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            isPollingStoppedRef.current = true;
        }
    };

    // Reset all states when modal opens
    useEffect(() => {
        if (!isOpen) {
            // Clean up when modal closes
            stopPolling();
            return;
        }

        // Reset states for new export
        setStatus('PENDING');
        setCounts(null);
        setCreatedAt(null);
        setExportMembers([]);
        setMembersTotalCount(0);
        setMembersPage(0);
        // setIsReady(false);
        isPollingStoppedRef.current = false;
        hasFetchedMembersRef.current = false;

        let pollInterval: NodeJS.Timeout | null = null;

        const checkExportStatus = async () => {
            // Don't check if polling is already stopped or members already fetched
            if (isPollingStoppedRef.current || hasFetchedMembersRef.current) return;

            try {
                const { token, tenantId, userId } = getAuthContext();
                if (!token) {
                    toast.error('Authentication required');
                    return;
                }


                // First, check the export status
                const exportResponse = await fetch(
                    `${BASE_URL}/v1/audience-insights/exports/${exportId}`,
                    {
                        method: 'GET',
                        headers: {
                            'X-Tenant-Id': tenantId,
                            'X-User-Id': userId || '',
                            'Authorization': `Bearer ${token}`,
                        },
                    }
                );

                if (!exportResponse.ok) {
                    throw new Error(`HTTP ${exportResponse.status}`);
                }

                const exportData = await exportResponse.json();

                setStatus(exportData.status || 'PENDING');
                setCounts(exportData.counts || null);
                setCreatedAt(exportData.createdAt || null);

                // When status becomes COMPLETED/SUCCESS, check snapshot status
                if ((exportData.status === 'COMPLETED' || exportData.status === 'SUCCESS') && !hasFetchedMembersRef.current) {

                    // Check snapshot status
                    const snapshotResponse = await fetch(
                        `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots/${snapshotId}`,
                        {
                            method: 'GET',
                            headers: {
                                'X-Tenant-Id': tenantId,
                                'X-User-Id': userId || '',
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    );

                    if (!snapshotResponse.ok) {
                        throw new Error(`HTTP ${snapshotResponse.status}`);
                    }

                    const snapshotData = await snapshotResponse.json();

                    // If snapshot is READY, fetch members
                    if (snapshotData.status === 'READY') {
                        // Stop polling first
                        stopPolling();
                        // Then fetch members
                        await fetchExportMembers(0);
                    }
                }
            } catch (error) {
                // console.error('❌ Failed to check export status:', error);
                toast.error('Failed to check export status');
            }
        };

        // Initial check
        checkExportStatus();

        // Start polling every 2 seconds
        pollInterval = setInterval(checkExportStatus, 2000);
        pollIntervalRef.current = pollInterval;

        // Cleanup on unmount or modal close
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
                pollIntervalRef.current = null;
            }
            isPollingStoppedRef.current = true;
        };
    }, [isOpen, exportId, snapshotId, segmentId]);

    const handleMembersPageChange = async (newPage: number) => {
        if (newPage < 0 || newPage >= Math.ceil(membersTotalCount / membersPerPage)) return;

        // Fetch members for new page - polling is already stopped
        try {
            setIsLoading(true);
            const { token, tenantId, userId } = getAuthContext();
            if (!token) {
                toast.error('Authentication required');
                setIsLoading(false);
                return;
            }

            const response = await fetch(
                `${BASE_URL}/v1/audience-insights/segments/${segmentId}/snapshots/${snapshotId}/members?size=${membersPerPage}&page=${newPage}`,
                {
                    method: 'GET',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            setExportMembers(data.content || []);
            setMembersPage(newPage);
        } catch (error) {
            // console.error('❌ Failed to fetch export members:', error);
            toast.error('Failed to load export members');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const totalMembersPages = Math.ceil(membersTotalCount / membersPerPage);
    const isExportReady = status === 'COMPLETED' || status === 'SUCCESS';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg w-[600px] max-w-[90%] max-h-[90vh] flex flex-col">
                {/* Fixed Header */}
                <div className="flex-shrink-0 p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-[#3F5265]">
                            Export Preview
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={isLoading}
                        >
                            <FiXComponent size={20} />
                        </button>
                    </div>

                    {/* Status Display */}
                    <div className="mt-2 flex gap-4 flex-wrap">
                        <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-[#657584]">Status:</span>
                            <span className={`text-sm font-semibold ${isExportReady ? 'text-green-600' : 'text-orange-500'
                                }`}>
                                {isExportReady ? 'Ready' : (
                                    <span className="flex items-center gap-1">
                                        Processing
                                        <span className="inline-flex gap-1">
                                            <span className="animate-bounce">.</span>
                                            <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                                            <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>.</span>
                                        </span>
                                    </span>
                                )}
                            </span>
                        </div>

                        {counts && isExportReady && (
                            <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-[#657584]">Total Members:</span>
                                <span className="text-sm text-[#3F5265]">{counts.total}</span>
                            </div>
                        )}

                        {createdAt && isExportReady && (
                            <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-[#657584]">Created At:</span>
                                <span className="text-sm text-[#3F5265]">
                                    {new Date(createdAt).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: true,
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {/* Loading State */}
                    {isLoading && exportMembers.length === 0 && isExportReady && (
                        <div className="text-center py-8 text-gray-500">Loading preview...</div>
                    )}

                    {/* Export Members Table - Auto displayed when ready */}
                    {isExportReady && !isLoading && (
                        <div className="mt-2">
                            {exportMembers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">No members found</div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Email
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {exportMembers.map((member: any, index: number) => (
                                                    <tr key={member.id || index}>
                                                        <td className="px-4 py-2 text-sm text-[#3F5265]">
                                                            {member.name || (member.firstName && member.lastName
                                                                ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                                                                : member.email || 'N/A')}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-[#657584]">
                                                            {member.email || 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination for members */}
                                    {totalMembersPages > 1 && (
                                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 mt-4">
                                            <span className="text-sm text-gray-500">
                                                Showing {exportMembers.length} members out of {membersTotalCount} total
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    disabled={membersPage === 0}
                                                    onClick={() => handleMembersPageChange(membersPage - 1)}
                                                    className="px-3 py-1 border rounded text-sm bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Prev
                                                </button>
                                                <span className="text-sm text-gray-600">
                                                    {membersPage + 1} of {totalMembersPages}
                                                </span>
                                                <button
                                                    disabled={membersPage === totalMembersPages - 1}
                                                    onClick={() => handleMembersPageChange(membersPage + 1)}
                                                    className="px-3 py-1 border rounded text-sm bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with Cancel and Download buttons */}
                <div className="flex-shrink-0 p-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <FiXCircleComponent size={16} />
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            onDownload();
                        }}
                        disabled={!isExportReady || isLoading}
                        className={`px-5 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${!isExportReady || isLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                            }`}
                    >
                        <FiDownloadComponent size={16} />
                        Download
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportPreviewModal;